import React, { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  RichTextEditor,
  RichTextEditorRef,
} from "@/components/ui/rich-text-editor";
import CompactFileUpload from "@/components/ui/compact-file-upload";
import {
  MoreHorizontal,
  Reply,
  Edit,
  Trash2,
  Paperclip,
  Download,
  Image,
  FileText,
  File,
  Archive,
  Send,
  ChevronDown,
  ChevronRight,
  Loader2,
  PlusCircle,
  MessageSquare,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format, formatDistanceToNow, isValid } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { CommentData, CommentAttachment } from "@/dto/add-comment-response.dto";
import { cn } from "@/lib/utils";

interface EnhancedCommentProps {
  comment: CommentData;
  projectMembers: Array<{ _id: string; fullName: string; email: string }>;
  onReply?: (
    parentId: string,
    content: string,
    richContent: string,
    attachments: File[],
    mentions: string[],
    targetUserName?: string
  ) => void;
  onEdit?: (
    commentId: string,
    content: string,
    richContent: string,
    attachments?: File[],
    replaceAttachments?: boolean
  ) => void;
  onDelete?: (commentId: string) => void;
  onMention?: (userId: string) => void;
  depth?: number;
  maxDepth?: number;
  isHighlighted?: boolean;
  highlightedCommentId?: string;
  highlightedReplyIds?: Set<string>; // New prop to highlight specific replies
  isLast?: boolean;
  hideHeader?: boolean; // New prop to hide header when used in timeline
  isReply?: boolean; // New prop to indicate if this is a reply (not a top-level comment)
  parentCommentId?: string; // ID of the root parent comment for flat replies
}

interface AttachmentFile extends File {
  id: string;
  preview?: string;
}

export const EnhancedComment: React.FC<EnhancedCommentProps> = ({
  comment,
  projectMembers,
  onReply,
  onEdit,
  onDelete,
  onMention,
  depth = 0,
  maxDepth = 3,
  isHighlighted = false,
  highlightedCommentId,
  highlightedReplyIds = new Set(),
  isLast = false,
  hideHeader = false,
  isReply = false,
  parentCommentId,
}) => {
  const { user } = useAuth();
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [repliesCollapsed, setRepliesCollapsed] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [replyRichContent, setReplyRichContent] = useState("");
  const [replyAttachments, setReplyAttachments] = useState<AttachmentFile[]>(
    []
  );
  const [replyMentions, setReplyMentions] = useState<string[]>([]);
  const [editContent, setEditContent] = useState(comment.content);
  const [editRichContent, setEditRichContent] = useState(
    comment.richContent || ""
  );
  const [editAttachments, setEditAttachments] = useState<AttachmentFile[]>([]);
  const [replaceAttachments, setReplaceAttachments] = useState(false);
  const [resetEditFileUpload, setResetEditFileUpload] = useState(0);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const replyEditorRef = useRef<RichTextEditorRef>(null);
  const editEditorRef = useRef<RichTextEditorRef>(null);

  const isCommentOwner =
    typeof comment.createdBy === "object"
      ? comment.createdBy._id === user?._id
      : comment.createdBy === user?._id;

  const isAdmin = user?.role === "Admin";
  const canEdit = isCommentOwner || isAdmin;
  const canDelete = isCommentOwner || isAdmin;

  const formatDate = (date: Date | string) => {
    const parsedDate = new Date(date);
    if (!isValid(parsedDate)) return "Invalid date";
    return format(parsedDate, "MMM d, yyyy 'at' h:mm a");
  };

  const getUserDisplayName = (user: CommentData["createdBy"]): string => {
    if (!user) return "Unknown User";
    if (typeof user === "string") return "User";

    const firstName = user.firstName ? user.firstName.trim() : "";
    const lastName = user.lastName ? user.lastName.trim() : "";

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    return user.email || "Unknown User";
  };

  const getUserInitials = (user: CommentData["createdBy"]) => {
    if (!user) return "UN";
    if (typeof user === "string") return "U";

    const firstName = user.firstName ? user.firstName.trim() : "";
    const lastName = user.lastName ? user.lastName.trim() : "";

    if (firstName || lastName) {
      return `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
    }

    return user.email?.[0]?.toUpperCase() || "U";
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) return <Image className="h-3 w-3" />;
    if (fileType === "application/pdf") return <FileText className="h-3 w-3" />;
    if (fileType.includes("zip") || fileType.includes("rar"))
      return <Archive className="h-3 w-3" />;
    return <File className="h-3 w-3" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  const handleReplySubmit = useCallback(() => {
    if (!replyContent.trim()) return;

    // If this is a reply to a reply, we need to reply to the root comment and mention the reply author
    const targetCommentId = isReply
      ? parentCommentId || comment._id
      : comment._id;
    const finalMentions = [...replyMentions];
    let targetUserName = "";

    // If replying to a reply, automatically mention the reply author
    if (
      isReply &&
      typeof comment.createdBy === "object" &&
      comment.createdBy._id !== user?._id
    ) {
      const replyAuthorId = comment.createdBy._id;
      targetUserName = getUserDisplayName(comment.createdBy);

      if (!finalMentions.includes(replyAuthorId)) {
        finalMentions.push(replyAuthorId);
      }
    }

    onReply?.(
      targetCommentId,
      replyContent,
      replyRichContent,
      replyAttachments,
      finalMentions,
      targetUserName
    );

    setIsReplying(false);
    setReplyContent("");
    setReplyRichContent("");
    setReplyAttachments([]);
    setReplyMentions([]);
    replyEditorRef.current?.clear();
  }, [
    comment._id,
    comment.createdBy,
    replyContent,
    replyRichContent,
    replyAttachments,
    replyMentions,
    onReply,
    isReply,
    parentCommentId,
    user?._id,
  ]);

  const handleEditSubmit = useCallback(async () => {
    if (!editContent.trim()) return;

    setIsEditSubmitting(true);
    try {
      await onEdit?.(
        comment._id,
        editContent,
        editRichContent,
        editAttachments,
        replaceAttachments
      );
      setIsEditing(false);
      setEditAttachments([]);
      setReplaceAttachments(false);
      setResetEditFileUpload((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to edit comment:", error);
    } finally {
      setIsEditSubmitting(false);
    }
  }, [
    comment._id,
    editContent,
    editRichContent,
    editAttachments,
    replaceAttachments,
    onEdit,
  ]);

  const handleMentionSelect = (mention: { id: string; label: string }) => {
    if (!replyMentions.includes(mention.id)) {
      setReplyMentions([...replyMentions, mention.id]);
    }
    onMention?.(mention.id);
  };

  const handleRemoveCurrentAttachment = useCallback(() => {
    setReplaceAttachments(true);
    setEditAttachments([]);
    setResetEditFileUpload((prev) => prev + 1);
  }, []);

  const handleEditAttachmentsChange = useCallback((files: AttachmentFile[]) => {
    setEditAttachments(files);
    if (files.length > 0) {
      setReplaceAttachments(true);
    }
  }, []);
  const handleStartReply = useCallback(() => {
    setIsReplying(true);

    // If replying to a reply, we'll insert the mention after the editor is mounted
    if (
      isReply &&
      typeof comment.createdBy === "object" &&
      comment.createdBy._id !== user?._id
    ) {
      const mentionId = comment.createdBy._id;
      const userName = getUserDisplayName(comment.createdBy);

      if (!replyMentions.includes(mentionId)) {
        setReplyMentions([mentionId]);
      } // Set initial content with proper mention format that TipTap recognizes
      // Use the exact structure that TipTap's mention extension expects
      const mentionHTML = `<span data-type="mention" data-id="${mentionId}" data-label="${userName}" class="mention bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 px-1 py-0.5 rounded text-sm font-medium">@${userName}</span>&nbsp;`; // Pre-populate the content states
      setReplyContent(`@${userName} `);
      setReplyRichContent(mentionHTML);

      // Focus the editor and position cursor after the mention
      setTimeout(() => {
        if (replyEditorRef.current) {
          replyEditorRef.current.focus();
          // Position cursor at the end of the content (after the mention)
          setTimeout(() => {
            replyEditorRef.current?.focusEnd();
          }, 50);
        }
      }, 100);
    } else {
      // For normal replies, ensure content is empty
      setReplyContent("");
      setReplyRichContent("");
    }
  }, [isReply, comment.createdBy, user?._id, replyMentions]);

  const mentionOptions = projectMembers
    .filter((member) => member._id !== user?._id) // Exclude current user from mentions
    .map((member) => ({
      id: member._id,
      label: member.fullName,
      email: member.email,
    }));

  const hasReplies = comment.replies && comment.replies.length > 0;

  return (
    <div
      className={cn(
        "group/comment relative",
        isHighlighted && "bg-primary/5 -mx-2 px-2 rounded-md"
      )}
    >
      <div className="flex gap-3">
        {/* Left Column: Avatar & Thread Line */}
        <div className="flex flex-col items-center shrink-0 relative">
          {!hideHeader ? (
            <Avatar className="h-9 w-9 z-10 border-2 border-background shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
              <AvatarFallback
                className={cn(
                  "text-[10px] font-bold",
                  typeof comment.createdBy === "object" &&
                    comment.createdBy.role === "Admin"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {getUserInitials(comment.createdBy)}
              </AvatarFallback>
            </Avatar>
          ) : (
            <div className="w-9" />
          )}

          {/* Thread Line - Connects to children */}
          {(hasReplies || isReplying) && !repliesCollapsed && (
            <div
              className="absolute top-9 left-1/2 -translate-x-1/2 w-[2px] h-[calc(100%-1rem)] bg-gradient-to-b from-border/60 to-transparent hover:bg-primary/40 transition-colors cursor-pointer group-hover/line:bg-primary/40"
              onClick={() => setRepliesCollapsed(true)}
              title="Collapse thread"
            />
          )}
        </div>

        {/* Right Column: Content & Children */}
        <div className="flex-1 min-w-0 pb-2">
          {/* Header */}
          {!hideHeader && (
            <div className="flex items-center gap-2 mb-1.5">
              <span className="font-semibold text-sm text-foreground/90">
                {getUserDisplayName(comment.createdBy)}
              </span>

              {typeof comment.createdBy === "object" &&
                comment.createdBy.role && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] h-5 px-1.5 font-medium text-muted-foreground bg-muted/50 border-0"
                  >
                    {comment.createdBy.role}
                  </Badge>
                )}

              <span className="text-xs font-medium text-muted-foreground/60">
                •{" "}
                {formatDistanceToNow(new Date(comment.createdAt), {
                  addSuffix: true,
                })}
              </span>

              {comment.isEdited && (
                <span className="text-[10px] text-muted-foreground italic">
                  (edited)
                </span>
              )}
            </div>
          )}

          {/* Collapsed State */}
          {repliesCollapsed && hasReplies ? (
            <div
              className="text-xs text-muted-foreground italic cursor-pointer hover:text-foreground mt-1"
              onClick={() => setRepliesCollapsed(false)}
            >
              <PlusCircle className="inline h-3 w-3 mr-1" />
              Thread hidden ({comment.replies?.length} replies)
            </div>
          ) : (
            <>
              {/* Comment Body */}
              <div className="text-sm text-foreground/80 leading-relaxed break-words">
                {isEditing ? (
                  <div className="space-y-3 mt-2">
                    <RichTextEditor
                      ref={editEditorRef}
                      content={editRichContent || editContent}
                      onChange={(text, html) => {
                        setEditContent(text);
                        setEditRichContent(html);
                      }}
                      mentions={mentionOptions}
                      placeholder="Edit your comment..."
                      className="min-h-[80px]"
                    />

                    <div className="space-y-2">
                      {comment.attachments &&
                        comment.attachments.length > 0 &&
                        !replaceAttachments && (
                          <div className="border rounded-md p-2 bg-muted/50 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm">
                              <Paperclip className="h-4 w-4" />
                              <span>
                                Current: {comment.attachments[0].name}
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleRemoveCurrentAttachment}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}

                      <div className="flex items-center gap-2">
                        <CompactFileUpload
                          onFilesChange={handleEditAttachmentsChange}
                          maxFiles={1}
                          maxSize={5 * 1024 * 1024}
                          resetTrigger={resetEditFileUpload}
                        />
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleEditSubmit}
                        disabled={isEditSubmitting || !editContent.trim()}
                      >
                        {isEditSubmitting ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          "Save"
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {comment.richContent ? (
                      <div
                        className="prose prose-sm max-w-none dark:prose-invert"
                        dangerouslySetInnerHTML={{
                          __html: comment.richContent,
                        }}
                      />
                    ) : (
                      <p className="whitespace-pre-wrap">{comment.content}</p>
                    )}

                    {/* Attachments */}
                    {comment.attachments && comment.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {comment.attachments.map((attachment) => (
                          <a
                            key={attachment._id}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 bg-card hover:bg-accent rounded-lg border border-border/40 shadow-sm transition-all text-xs font-medium group/file hover:-translate-y-0.5 hover:shadow-md"
                          >
                            {getFileIcon(attachment.fileType)}
                            <span className="max-w-[150px] truncate text-foreground/80">
                              {attachment.name}
                            </span>
                            <Download className="h-3 w-3 opacity-0 group-hover/file:opacity-100 transition-opacity text-primary" />
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Action Bar */}
              <div className="flex items-center gap-4 mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-muted-foreground hover:text-primary hover:bg-primary/5 text-xs font-medium rounded-full transition-colors"
                  onClick={handleStartReply}
                >
                  <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                  Reply
                </Button>

                {(canEdit || canDelete) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-0 text-muted-foreground hover:text-foreground text-xs font-medium"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {canEdit && (
                        <DropdownMenuItem onClick={() => setIsEditing(true)}>
                          <Edit className="mr-2 h-3 w-3" /> Edit
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <DropdownMenuItem
                          onClick={() => onDelete?.(comment._id)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-3 w-3" /> Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Reply Input */}
              {isReplying && (
                <div className="mt-4 animate-in fade-in slide-in-from-top-2 relative">
                  <div className="relative bg-card border border-primary/20 rounded-xl shadow-lg p-4 ring-1 ring-primary/5">
                    <div className="absolute -top-3 left-4 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                      <Reply className="h-3 w-3" />
                      Replying to {getUserDisplayName(comment.createdBy)}
                    </div>
                    <RichTextEditor
                      ref={replyEditorRef}
                      content={replyRichContent}
                      onChange={(text, html) => {
                        setReplyContent(text);
                        setReplyRichContent(html);
                      }}
                      mentions={mentionOptions}
                      onMentionSelect={handleMentionSelect}
                      placeholder="Write a reply..."
                      className="min-h-[100px] bg-background/50 shadow-inner border-border/60 focus-within:ring-2 focus-within:ring-primary/20 transition-all rounded-lg"
                    />
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/40">
                      <CompactFileUpload
                        onFilesChange={setReplyAttachments}
                        maxFiles={3}
                        maxSize={5 * 1024 * 1024}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setIsReplying(false)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleReplySubmit}
                          disabled={!replyContent.trim()}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all px-4"
                        >
                          Reply
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Nested Replies */}
              {hasReplies && (
                <div className="mt-3 space-y-4">
                  {comment.replies!.map((reply) => (
                    <div key={reply._id} className="relative">
                      <EnhancedComment
                        comment={reply}
                        projectMembers={projectMembers}
                        onReply={onReply}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onMention={onMention}
                        depth={depth + 1}
                        maxDepth={maxDepth}
                        isHighlighted={highlightedCommentId === reply._id}
                        highlightedCommentId={highlightedCommentId}
                        highlightedReplyIds={highlightedReplyIds}
                        hideHeader={false}
                        isReply={true}
                        parentCommentId={parentCommentId || comment._id}
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
